// src/components/Staerkemeldung.js
import React, { useState, useRef, useEffect } from "react";
import { QRCodeSVG } from "qrcode.react";
import SignatureCanvas from "react-signature-canvas";
import { doc, setDoc, onSnapshot, getDoc } from "firebase/firestore";
import { getFirestore } from "firebase/firestore";
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { db } from "../firebaseConfig";
import "./Staerkemeldung.css";
import { getCustomStaerkemeldungPositions, addCustomStaerkemeldungPosition, removeCustomStaerkemeldungPosition } from '../services/firebaseDienstplanService';

// Die öffentliche URL der Firebase-Hosting-App
const APP_URL = "https://brandschutz-app.web.app";

const STANDARD_POSITIONEN = [
  { projekt: 'FDB', position: 'Teamleiter' },
  { projekt: 'FDB', position: 'Brandschützer 1' },
  { projekt: 'FDB', position: 'Brandschützer 2' },
  { projekt: 'FDB', position: 'Zugang 1' },
  { projekt: 'FDB', position: 'Zugang 2' }
];

const Staerkemeldung = () => {
  // Standard-Datum ist heute
  const today = new Date().toISOString().split('T')[0];
  const [dienst, setDienst] = useState('Tagdienst');
  const [customPositions, setCustomPositions] = useState([]); // {projekt, position, id}
  const [loading, setLoading] = useState(true);
  const [customPosition, setCustomPosition] = useState("");
  const [stellen, setStellen] = useState(() => STANDARD_POSITIONEN.map(p => ({ ...p, name: '', vorname: '', unterschrift: null, signaturId: generateSignatureId() })));
  
  // State für aktuelle Signatur
  const [currentSignatureIndex, setCurrentSignatureIndex] = useState(null);
  const [signatureMode, setSignatureMode] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [useDirectSignature, setUseDirectSignature] = useState(false);
  const [waitingForSignature, setWaitingForSignature] = useState(false);
  
  // Refs
  const fileInputRef = useRef(null);
  const signaturePadRef = useRef(null);
  const unsubscribeRef = useRef(null);
  
  // Generiert eine eindeutige ID für die Signatur
  function generateSignatureId() {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  }
  
  // Firestore-Positionen laden und in die Tabelle einfügen
  useEffect(() => {
    const fetchPositions = async () => {
      setLoading(true);
      const result = await getCustomStaerkemeldungPositions();
      setCustomPositions(result.map(p => ({ projekt: 'FDB', position: p.name, id: p.id })));
      setLoading(false);
    };
    fetchPositions();
  }, []);

  // Dynamische Zusammenführung der Positionen
  useEffect(() => {
    setStellen(prev => {
      // Standard-Positionen immer zuerst
      const base = STANDARD_POSITIONEN.map(p => {
        const found = prev.find(s => s.position === p.position && s.projekt === p.projekt);
        return found ? found : { ...p, name: '', vorname: '', unterschrift: null, signaturId: generateSignatureId() };
      });
      // Custom-Positionen hinten anfügen
      const customs = customPositions.map(p => {
        const found = prev.find(s => s.position === p.position && s.projekt === p.projekt);
        return found ? found : { ...p, name: '', vorname: '', unterschrift: null, signaturId: generateSignatureId() };
      });
      return [...base, ...customs];
    });
    // eslint-disable-next-line
  }, [customPositions]);

  // Position hinzufügen
  const handleAddCustomPosition = async () => {
    if (customPosition.trim()) {
      await addCustomStaerkemeldungPosition(customPosition.trim());
      const result = await getCustomStaerkemeldungPositions();
      setCustomPositions(result.map(p => ({ projekt: 'FDB', position: p.name, id: p.id })));
      setCustomPosition("");
    }
  };

  // Position entfernen (nur benutzerdefinierte)
  const handleRemovePosition = async (row) => {
    if (row.id) {
      await removeCustomStaerkemeldungPosition(row.id);
      const result = await getCustomStaerkemeldungPositions();
      setCustomPositions(result.map(p => ({ projekt: 'FDB', position: p.name, id: p.id })));
      setStellen(prev => prev.filter(s => !(s.position === row.position && s.projekt === row.projekt)));
    }
  };
  
  // Handler für Text-Änderungen
  const handleTextChange = (index, field, value) => {
    setStellen(prev => {
      const updated = [...prev];
      updated[index][field] = value;
      return updated;
    });
  };
  
  // QR-Code für Unterschrift generieren
  const handleQRCodeClick = (index, e) => {
    // Verhindern von Standard-Navigationsverhalten
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Lokale Unterschrift-Option anzeigen, wenn auf dem gleichen Gerät
    if (isMobileDevice()) {
      setCurrentSignatureIndex(index);
      setSignatureMode(true);
      setUseDirectSignature(true);
    } else {
      // Setze den Index des aktuellen Unterschriftsfeldes
      setCurrentSignatureIndex(index);
      
      // Starte den Firestore-Listener für diese Unterschrift
      setupSignatureListener(index);
      
      // Zeige QR-Code-Modal an
      setSignatureMode(true);
      setUseDirectSignature(false);
    }
  };
  
  // Firestore-Echtzeitlistener einrichten
  const setupSignatureListener = (index) => {
    const signatureId = stellen[index]?.signaturId;
    
    // Stoppen Sie frühere Listener, falls vorhanden
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
    }
    
    setWaitingForSignature(true);
    
    // Prüfen, ob bereits eine Unterschrift existiert
    const checkExistingSignature = async () => {
      try {
        const signatureDocRef = doc(db, "signatures", signatureId);
        const docSnap = await getDoc(signatureDocRef);
        
        if (docSnap.exists() && docSnap.data().signature) {
          // Unterschrift bereits vorhanden, aktualisieren
          const updatedStellen = [...stellen];
          updatedStellen[index].unterschrift = docSnap.data().signature;
          
          setStellen(updatedStellen);
        }
      } catch (error) {
        console.error("Fehler beim Abrufen vorhandener Unterschrift:", error);
      }
    };
    
    checkExistingSignature();
    
    // Echtzeit-Listener für das Dokument einrichten
    const signatureDocRef = doc(db, "signatures", signatureId);
    unsubscribeRef.current = onSnapshot(signatureDocRef, (docSnapshot) => {
      if (docSnapshot.exists() && docSnapshot.data().signature) {
        // Unterschrift gefunden
        const updatedStellen = [...stellen];
        updatedStellen[index].unterschrift = docSnapshot.data().signature;
        
        setStellen(updatedStellen);
        
        // Listener stoppen und Modal schließen
        stopListener();
        setSignatureMode(false);
        
        setMessage({
          text: `Unterschrift für ${stellen[index].position} erfolgreich empfangen!`,
          type: "success"
        });
      }
    }, (error) => {
      console.error("Fehler beim Firestore-Listener:", error);
      setMessage({
        text: "Fehler beim Abhören auf neue Unterschriften.",
        type: "error"
      });
    });
  };
  
  // Listener stoppen
  const stopListener = () => {
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }
    setWaitingForSignature(false);
  };
  
  // Cleanup bei Komponenten-Unmount
  useEffect(() => {
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, []);
  
  // Prüfen, ob ein mobiles Gerät verwendet wird
  const isMobileDevice = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  };
  
  // Unterschrift von Datei hochladen
  const handleSignatureUpload = (e) => {
    if (!e.target.files || !e.target.files[0]) return;
    
    const file = e.target.files[0];
    const reader = new FileReader();
    
    reader.onload = (event) => {
      const signatureDataURL = event.target.result;
      saveSignatureToState(signatureDataURL);
      // Auch in Firestore speichern
      saveSignatureToFirestore(signatureDataURL);
    };
    
    reader.readAsDataURL(file);
  };
  
  // Direkte Unterschrift aus dem SignatureCanvas speichern
  const saveSignatureFromPad = () => {
    if (signaturePadRef.current && !signaturePadRef.current.isEmpty()) {
      // Als Bild speichern (Base64-String)
      const signatureDataURL = signaturePadRef.current.toDataURL('image/png');
      saveSignatureToState(signatureDataURL);
      
      // Auch in Firestore speichern
      saveSignatureToFirestore(signatureDataURL);
    } else {
      setMessage({
        text: "Bitte unterschreiben Sie, bevor Sie speichern.",
        type: "error"
      });
    }
  };
  
  // Unterschrift in Firestore speichern
  const saveSignatureToFirestore = async (signatureData) => {
    const signatureId = stellen[currentSignatureIndex]?.signaturId;
    
    try {
      await setDoc(doc(db, "signatures", signatureId), {
        signature: signatureData,
        timestamp: new Date(),
        position: stellen[currentSignatureIndex]?.position,
        name: stellen[currentSignatureIndex]?.name,
        vorname: stellen[currentSignatureIndex]?.vorname
      });
      
      console.log(`Unterschrift für ID ${signatureId} in Firestore gespeichert`);
    } catch (error) {
      console.error("Fehler beim Speichern der Unterschrift:", error);
      setMessage({
        text: "Fehler beim Speichern der Unterschrift.",
        type: "error"
      });
    }
  };
  
  // Unterschrift in den State speichern
  const saveSignatureToState = (signatureData) => {
    setStellen(prev => {
      const updated = [...prev];
      if (currentSignatureIndex !== null) {
        updated[currentSignatureIndex].unterschrift = signatureData;
      }
      return updated;
    });
    
    setSignatureMode(false);
    setMessage({ 
      text: `Unterschrift für ${stellen[currentSignatureIndex]?.position} erfolgreich gespeichert`,
      type: "success" 
    });
  };
  
  // PDF generieren und als E-Mail versenden sowie lokal speichern
  const handleSendAndSave = () => {
    // Die Prüfung auf fehlende Felder wird entfernt
    try {
      const pdf = new jsPDF();
      pdf.setFontSize(20);
      pdf.text(`Stärkemeldung ${dienst === 'Tagdienst' ? 'Tagschicht' : 'Nachtschicht'}`, 105, 20, { align: 'center' });
      pdf.setFontSize(12);
      pdf.text(`Datum: ${today}`, 20, 30);
      pdf.text(`Projekt: FDB`, 20, 38);
      const tableData = stellen.map(stelle => [
        stelle.position,
        stelle.name,
        stelle.vorname,
        "" // Unterschrift-Spalte bleibt leer, Bild wird später eingefügt
      ]);
      autoTable(pdf, {
        startY: 45,
        head: [['Position', 'Name', 'Vorname', 'Unterschrift']],
        body: tableData,
        didDrawCell: function (data) {
          if (data.section === 'body' && data.column.index === 3 && stellen[data.row.index].unterschrift) {
            const img = stellen[data.row.index].unterschrift;
            const x = data.cell.x + 2;
            const y = data.cell.y + 2;
            const cellHeight = data.cell.height - 4;
            const cellWidth = data.cell.width - 4;
            const imgWidth = Math.min(cellWidth, 40);
            const imgHeight = Math.min(cellHeight, 20);
            pdf.addImage(img, 'PNG', x, y, imgWidth, imgHeight);
          }
        }
      });
      const now = new Date();
      const timestamp = now.toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const fileName = `Staerkemeldung_FDB_${today}_${timestamp}.pdf`;
      pdf.save(fileName);
      setMessage({ text: "PDF wurde gespeichert. Bitte hängen Sie die heruntergeladene PDF-Datei an die E-Mail an.", type: "success" });
    } catch (error) {
      setMessage({ text: "Fehler: " + error.message, type: "error" });
    }
  };
  
  // Button-Event für Signaturmodus abbrechen
  const cancelSignature = () => {
    stopListener(); // Listener stoppen, falls aktiv
    setSignatureMode(false);
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
    }
  };
  
  // Unterschrift-Zeichenfläche löschen
  const clearSignature = () => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
    }
  };
  
  // Generiert einen Link für die mobile Unterschrift
  const getMobileSignatureLink = (index) => {
    const signatureId = stellen[index]?.signaturId;
    const position = stellen[index]?.position;
    const name = stellen[index]?.name || '';
    const vorname = stellen[index]?.vorname || '';
    
    return `${APP_URL}/mobile-signature?id=${signatureId}&position=${encodeURIComponent(position)}&name=${encodeURIComponent(name)}&vorname=${encodeURIComponent(vorname)}`;
  };
  
  return (
    <div className="staerkemeldung-flexbox">
      <div className="staerkemeldung-card">
        <h2 className="staerkemeldung-title">Stärkemeldung – {dienst === 'Tagdienst' ? 'Tagschicht' : 'Nachtschicht'}</h2>
        <div className="staerkemeldung-controls">
          <label>Dienst:</label>
          <select value={dienst} onChange={e => setDienst(e.target.value)}>
            <option value="Tagdienst">Tagdienst</option>
            <option value="Nachtdienst">Nachtdienst</option>
          </select>
          <input
            type="text"
            value={customPosition}
            onChange={e => setCustomPosition(e.target.value)}
            placeholder="Zusätzliche Position eingeben"
            disabled={loading}
            className="input-add-pos"
          />
          <button className="btn-add-pos" onClick={handleAddCustomPosition} disabled={loading || !customPosition.trim()}>Hinzufügen</button>
        </div>
        <div className="staerkemeldung-table-wrapper">
          <table className="staerkemeldung-table">
            <thead>
              <tr>
                <th>Projekt</th>
                <th>Position</th>
                <th>Name</th>
                <th>Vorname</th>
                <th>Unterschrift</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {stellen.map((row, idx) => (
                <tr key={row.projekt + '|' + row.position} className={idx % 2 === 0 ? 'zebra' : ''}>
                  <td className="projekt-cell">{idx === 0 || stellen[idx-1].projekt !== row.projekt ? row.projekt : ''}</td>
                  <td>{row.position}</td>
                  <td>
                    <input
                      type="text"
                      placeholder="Nachname"
                      value={row.name || ''}
                      onChange={e => handleTextChange(idx, "name", e.target.value)}
                      className="input-name"
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      placeholder="Vorname"
                      value={row.vorname || ''}
                      onChange={e => handleTextChange(idx, "vorname", e.target.value)}
                      className="input-name"
                    />
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {row.unterschrift ? (
                      <img 
                        src={row.unterschrift} 
                        alt="Unterschrift" 
                        className="unterschrift-preview"
                        onClick={() => handleQRCodeClick(idx)}
                      />
                    ) : (
                      <button 
                        className="qr-btn"
                        onClick={e => handleQRCodeClick(idx, e)}
                        title="QR-Code scannen oder tippen für Unterschrift"
                      >
                        <div className="qr-btn-inner">
                          <QRCodeSVG
                            value={getMobileSignatureLink(idx)}
                            size={40}
                          />
                        </div>
                        <span className="qr-btn-text">QR scannen/tippen</span>
                      </button>
                    )}
                  </td>
                  <td>
                    {row.id && (
                      <button onClick={() => handleRemovePosition(row)} className="btn-remove-pos" title="Position löschen">×</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="staerkemeldung-btn-container">
          <button className="btn-main" onClick={handleSendAndSave}>Stärkemeldung versenden/sichern</button>
        </div>
      </div>
      
      {/* QR-Code Modal */}
      {signatureMode && !useDirectSignature && (
        <div className="qr-modal-overlay">
          <div className="qr-modal-content">
            <div className="qr-modal-header">
              <h3>QR-Code für Unterschrift</h3>
              <button className="qr-modal-close" onClick={cancelSignature}>×</button>
            </div>
            <div className="qr-modal-body">
              <p>Scannen Sie diesen QR-Code mit Ihrem Smartphone, um zu unterschreiben:</p>
              <div className="qr-code-container">
                <QRCodeSVG
                  value={getMobileSignatureLink(currentSignatureIndex)}
                  size={300}
                />
              </div>
              <p className="qr-instructions">
                <strong>Position:</strong> {stellen[currentSignatureIndex]?.position}<br/>
                <strong>Name:</strong> {stellen[currentSignatureIndex]?.name || 'Nicht ausgefüllt'} {stellen[currentSignatureIndex]?.vorname || ''}
              </p>
              {waitingForSignature && (
                <div className="waiting-message">
                  <div className="spinner"></div>
                  <p>Warte auf Unterschrift...</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Direkte Unterschrift Modal */}
      {signatureMode && useDirectSignature && (
        <div className="qr-modal-overlay">
          <div className="qr-modal-content">
            <div className="qr-modal-header">
              <h3>Direkte Unterschrift</h3>
              <button className="qr-modal-close" onClick={cancelSignature}>×</button>
            </div>
            <div className="qr-modal-body">
              <p>Unterschreiben Sie direkt hier:</p>
              <div className="signature-pad-container">
                <SignatureCanvas
                  ref={signaturePadRef}
                  penColor="black"
                  backgroundColor="white"
                  minWidth={2}
                  maxWidth={3}
                  throttle={16}
                  velocityFilterWeight={0.7}
                  onBegin={() => {
                    if (signaturePadRef.current && signaturePadRef.current._canvas) {
                      signaturePadRef.current._canvas.focus();
                    }
                  }}
                  onEnd={() => {
                    if (signaturePadRef.current && signaturePadRef.current._canvas) {
                      signaturePadRef.current._canvas.style.touchAction = 'none';
                    }
                  }}
                  canvasProps={{
                    className: "signature-canvas",
                    style: {
                      touchAction: 'none',
                      width: '400px',
                      height: '200px'
                    },
                    onTouchStart: (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    },
                    onTouchMove: (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    },
                    onTouchEnd: (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }
                  }}
                />
              </div>
              <div className="signature-actions">
                <button onClick={clearSignature}>Löschen</button>
                <button onClick={saveSignatureFromPad}>Speichern</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Staerkemeldung;

